/**
 * One choice in a row of chips — the repeat presets, the Custom units, Ends.
 * A radio: one chip per group is selected. The inks are the AA-safe pair;
 * TabSegment's chip variant reads `text.dim`, which fails 4.5:1 in light.
 */
import React from 'react';
import { StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';

interface ChoiceChipProps {
  label: string;
  selected: boolean;
  onPress: () => void;
  /** Spoken name when it differs from the visible label. */
  accessibilityLabel?: string;
}

export const ChoiceChip: React.FC<ChoiceChipProps> = ({
  label,
  selected,
  onPress,
  accessibilityLabel,
}) => {
  const type = useTypography();
  const space = useSpacing();
  const radius = useRadius();
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      chip: {
        minHeight: 32,
        justifyContent: 'center',
        paddingHorizontal: space.md,
        borderRadius: radius.full,
        borderWidth: 1,
        borderColor: colors.card.border,
        backgroundColor: colors.glass.buttonBg,
      },
      chipSelected: {
        borderColor: colors.newsfeed.tagActiveBorder,
        backgroundColor: colors.accent.blueBgStrong,
      },
      chipText: {
        ...type.buttonSmall,
        color: semantic.text.secondary,
      },
      chipTextSelected: {
        color: semantic.interactive.primaryHover,
      },
    }),
  );

  return (
    <Hoverable
      hover={selected ? 'none' : 'glass'}
      style={[styles.chip, selected && styles.chipSelected]}
      onPress={onPress}
      // 32pt chip + 6 a side = the 44pt target.
      hitSlop={{ top: 6, bottom: 6 }}
      accessibilityRole="radio"
      accessibilityState={{ checked: selected }}
      accessibilityLabel={accessibilityLabel ?? label}
    >
      <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{label}</Text>
    </Hoverable>
  );
};

ChoiceChip.displayName = 'ChoiceChip';
